import { capitalize } from 'lodash-es';
import { create, v, w } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { dimensions } from '@dojo/framework/core/middleware/dimensions';
import { Button } from '@dojo/widgets/button';
import { Tooltip, Orientation } from '@dojo/widgets/tooltip';
import has from '@dojo/framework/core/has';
import { Icon } from '@holmescorp/base-widgets';
import { createNlsMiddleware, userstate, lccstate, expandedTopicChange } from '@holmescorp/lcc-state';
import { Card } from '../../widgets/index';
import { ExpandedTopicCard } from './expendedTopic';
import css from '../../css/tgf.m.css';
const icache = createICacheMiddleware();
const ditabookNls = createNlsMiddleware('ditabook');
const factory = create({
    icache,
    theme,
    ditabookNls,
    dimensions,
    userstate,
    lccstate
}).properties();
export const TGFCard = factory(({ id, properties, middleware: { icache, theme, dimensions, ditabookNls, userstate, lccstate } }) => {
    const themeCss = theme.classes(css);
    const { xqa, key, domain, completed, percent, title, topic, pretest = 0, isreadingstarted = false, isreadingcompleted = false, isquizstarted = false, isquizcompleted = false, ispromotable = true, ispromoted = false, action } = properties();
    const { messages: { topic_domain_mappings = '{}', pretest_indicator_threshold = '66.7', quiz_score_threshold = '80', card_button_label_start = '[ditabook] card_button_label_start', card_button_label_middle = '[ditabook] card_button_label_middle', card_button_label_finished = '[ditabook] card_button_label_finished', card_subtitle_reading_start = '[ditabook] card_subtitle_reading_start', card_subtitle_reading_continue = '[ditabook] card_subtitle_reading_continue', card_subtitle_reading_finish = '[ditabook] card_subtitle_reading_finish', card_subtitle_quiz_start = '[ditabook] card_subtitle_quiz_start', card_subtitle_quiz_finish = '[ditabook] card_subtitle_quiz_finish', card_promote_tooltip = '', card_demote_tooltip = '', conditional_topic_filters = '{}', conditional_filter_state_key = 'landing', recommended_to_flag = '3' } } = ditabookNls();
    const { get, path } = userstate;
    let domainMappings = {};
    let parsedFilters = {};
    try {
        domainMappings = JSON.parse(topic_domain_mappings);
        parsedFilters = JSON.parse(conditional_topic_filters);
    }
    catch (e) {
        domainMappings = {};
        parsedFilters = {};
    }
    const domainLabel = domainMappings[key] || domain;
    const isFlaggable = (key) => {
        const toolstate = get(path('toolstate', conditional_filter_state_key));
        if (key in parsedFilters === false || !toolstate) {
            return true;
        }
        const filter = parsedFilters[key];
        return filter && toolstate[filter.filterby] && toolstate[filter.filterby] === filter.condition;
    };
    const assessmentResults = get(path('assessment')) || {};
    let flagged = false;
    if (has('use-ml-study-plan')) {
        const assessOrder = assessmentResults[key] && assessmentResults[key].data.order;
        flagged =
            isFlaggable(key) &&
                assessmentResults[key] &&
                assessOrder != null &&
                assessOrder < parseInt(recommended_to_flag);
    }
    else {
        const assessScore = assessmentResults[key] && assessmentResults[key].data.percent;
        flagged =
            isFlaggable(key) && assessmentResults[key] && assessScore < parseFloat(pretest_indicator_threshold);
    }
    const activitiesCompleted = isreadingcompleted && isquizcompleted;
    const which_label = () => {
        const pretestThreshold = parseFloat(quiz_score_threshold);
        if (isquizcompleted ||
            (pretest >= pretestThreshold && !isreadingstarted) ||
            (!isquizcompleted && !isquizstarted && isreadingcompleted)) {
            return card_button_label_finished;
        }
        if (isquizstarted) {
            return pretest >= pretestThreshold && !isreadingstarted
                ? card_button_label_finished
                : card_button_label_middle;
        }
        if (!isreadingstarted && pretest < pretestThreshold) {
            return card_button_label_start;
        }
        return card_button_label_middle;
    };
    const which_subtitle = () => {
        if (activitiesCompleted) {
            return '';
        }
        if (isquizstarted && (!isreadingstarted || isreadingcompleted)) {
            return card_subtitle_quiz_finish;
        }
        if (!isreadingstarted) {
            return card_subtitle_reading_start;
        }
        if (isreadingcompleted && percent === 0) {
            return card_subtitle_quiz_start;
        }
        if (isreadingstarted) {
            return isquizcompleted ? card_subtitle_reading_finish : card_subtitle_reading_continue;
        }
        return '';
    };
    const promote_actions = v('div', {
        classes: [themeCss.promote, !ispromotable ? themeCss.disabledPromote : null, 'cardActions'],
        xqa: 'tgf-card-actions',
        onclick: () => {
            if (ispromotable && action) {
                action(key);
            }
            icache.set('promote_ttopen', false);
            icache.set('demote_ttopen', false);
        }
    }, [
        ispromoted
            ? w(Tooltip, {
                open: icache.getOrSet('demote_ttopen', false),
                orientation: Orientation.top
            }, [
                {
                    content: card_demote_tooltip,
                    trigger: v('span', {
                        onmouseover: () => ispromotable && icache.set('demote_ttopen', true),
                        onmouseout: () => ispromotable && icache.set('demote_ttopen', false),
                        onfocus: () => ispromotable && icache.set('demote_ttopen', true),
                        onblur: () => ispromotable && icache.set('demote_ttopen', false),
                        xqa: 'tgf-card-action-demote'
                    }, [
                        w(Icon, {
                            fapre: 'far',
                            faname: 'square-minus'
                        })
                    ])
                }
            ])
            : w(Tooltip, {
                open: icache.getOrSet('promote_ttopen', false),
                orientation: Orientation.top
            }, [
                {
                    content: card_promote_tooltip,
                    trigger: v('span', {
                        onmouseover: () => ispromotable && icache.set('promote_ttopen', true),
                        onmouseout: () => ispromotable && icache.set('promote_ttopen', false),
                        onfocus: () => ispromotable && icache.set('promote_ttopen', true),
                        onblur: () => ispromotable && icache.set('promote_ttopen', false),
                        xqa: 'tgf-card-action-promote'
                    }, [
                        w(Icon, {
                            fapre: 'far',
                            faname: 'square-plus'
                        })
                    ])
                }
            ])
    ]);
    const { get: lget, path: lpath } = lccstate;
    const expandedTopic = lget(lpath('expanded_topic'));
    const isExpanded = expandedTopic === key;
    if (isExpanded) {
        expandedTopicChange({ topicid: '' });
    }
    const expanded = icache.getOrSet('expanded', isExpanded);
    console.info(title, completed, percent);
    return v('div', { id, key, xqa, classes: [theme.variant(), themeCss.root] }, [
        expanded
            ? w(ExpandedTopicCard, {
                expanded,
                topic,
                xqa: `${key}-expanded`,
                height: dimensions.get(key).size.height,
                onRequestClose: () => {
                    icache.set('expanded', false);
                }
            })
            : null,
        w(Card, {
            xqa: `card-${domain}-${key}`,
            mediaSrc: `${window.hcstatic.portal_cdn}/js/images/${window.hcstatic.portal_site}/card_header_${key}.png`,
            mediaTitle: capitalize(domain),
            disabled: activitiesCompleted
        }, [
            {
                content: v('div', { classes: [themeCss.content] }, [
                    ispromoted === undefined ? null : promote_actions,
                    v('div', { classes: [themeCss.domain], xqa: 'tgf-card-domain-label' }, [domainLabel]),
                    v('div', { classes: [themeCss.title], xqa: 'tgf-card-title' }, [title]),
                    v('div', { classes: [themeCss.subtitle], xqa: 'tgf-card-subtitle' }, [which_subtitle()]),
                    v('div', { classes: [themeCss.actions], xqa: 'tgf-card-button' }, [
                        w(Button, {
                            onClick: () => {
                                icache.set('expanded', true);
                            }
                        }, [
                            {
                                label: which_label()
                            }
                        ])
                    ]),
                    flagged
                        ? v('div', { classes: [themeCss.indicator], xqa: 'tgf-card-indicator' }, [
                            v('span', { classes: ['fa-stack', 'fa-1x'] }, [
                                v('i', { classes: ['fa-regular', 'fa-circle', 'fa-stack-2x'] }),
                                v('i', {
                                    classes: ['fa-solid', 'fa-exclamation', 'fa-stack-1x']
                                })
                            ])
                        ])
                        : null
                ])
            }
        ])
    ]);
});
export default TGFCard;
